/* ══════════════════════════════════════════════════════════════════════════
   board.js — the ESP32-P4 node, drawn in three dimensions.

   Every connector on the plane is real hardware on the
   Waveshare ESP32-P4-WIFI6-POE-ETH. Which of them is *bound to a runtime
   function* is W8, and W8 has no backend yet — the page says so.
   ══════════════════════════════════════════════════════════════════════════ */

import { useState, useRef, useCallback, useEffect } from "react";
import { useMotionValue, useSpring, useMotionTemplate } from "framer-motion";
import { html, M, F, Ico, FADE, stop } from "./ui.js";
import * as E from "./edge-data.js";

const TONE = { live:"var(--aqua)", partial:"var(--amber)", idle:"var(--fg-3)", free:"rgba(255,255,255,.22)" };
/* Centre of the core module as it actually sits — upper-middle, not the middle
   of the board. Traces fan out from where the part is, so a trace to the RJ45
   now runs the length it really runs. */
/* Centre of the P4 shield on the real 55 × 68 mm board (Y-down percentages). */
const MCU = { x:42.3, y:32.5 };

/* traces run from the die out to each connector — an active one flows */
function Traces({ ports, hot }){
  return html`
    <svg className="pcb__traces" viewBox="0 0 100 100" preserveAspectRatio="none">
      ${ports.map(p=>{
        const tx = p.x + p.w/2, ty = p.y + p.h/2;
        const midX = Math.abs(tx-MCU.x) > Math.abs(ty-MCU.y) ? tx : MCU.x;
        const d = `M ${MCU.x} ${MCU.y} L ${midX} ${MCU.y} L ${midX} ${ty} L ${tx} ${ty}`;
        const on = p.state === "live" || p.state === "partial";
        return html`<${F} key=${p.id}>
          <path d=${d} fill="none" vectorEffect="non-scaling-stroke"
            stroke=${on ? TONE[p.state] : "rgba(255,255,255,.09)"}
            strokeWidth=${hot===p.id ? 1.6 : on ? .9 : .55}
            strokeOpacity=${hot===p.id ? 1 : on ? .5 : .5}/>
          ${on && html`<path className="trace-flow" d=${d} fill="none" vectorEffect="non-scaling-stroke"
            stroke=${TONE[p.state]} strokeWidth="1.5" strokeLinecap="round"/>`}
        <//>`;
      })}
    </svg>`;
}

function Comp({ p, hot, sel, onHover, onPick }){
  const lit = p.state === "live" || p.state === "partial";
  const c = TONE[p.state];
  const h = p.cls === "radio" ? 16 : p.cls === "power" ? 22 : p.cls === "storage" ? 12 : 8;
  return html`
    <div className="comp" data-on=${lit?"1":"0"} data-sel=${sel?"1":"0"}
        style=${{ left:p.x+"%", top:p.y+"%", width:p.w+"%", height:p.h+"%", "--h":h+"px", "--c":c }}
        onMouseEnter=${()=>onHover(p.id)} onMouseLeave=${()=>onHover(null)}
        onClick=${e=>{ stop(e); onPick(p.id); }}>
      <i className="comp__side"/>
      ${/* A label wider than the part it sits on truncates to two letters and
            stops being a label. The small connectors say who they are on hover
            and in the list below instead. */""}
      <i className="comp__top">${p.w >= 14 ? html`<b>${p.label}</b>` : null}</i>
      ${lit && html`<${F}>
        <i className="comp__glow"/>
        <i className="beam" style=${{ "--beam": (p.state==="live"?54:34)+"px" }}/>
        <i className="beam beam--halo"/>
      <//>`}
      ${hot && html`<div className="comp__tip" onClick=${stop}>
        <b>${p.full}</b><span>${p.bound || "unassigned"}</span></div>`}
    </div>`;
}

export function Board({ ports, sel, onPick }){
  const [hot,setHot] = useState(null);
  const [spin,setSpin] = useState(true);
  const wrap = useRef(null);
  const rx = useMotionValue(58), rz = useMotionValue(-32);
  const srx = useSpring(rx,{ stiffness:340, damping:34 });
  const srz = useSpring(rz,{ stiffness:340, damping:34 });
  const tf = useMotionTemplate`rotateX(${srx}deg) rotateZ(${srz}deg)`;
  const drag = useRef(null);

  useEffect(()=>{
    if(!spin) return;
    let raf, t0 = performance.now();
    const loop = t => { rz.set(-32 + Math.sin((t-t0)/4200)*13); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return ()=>cancelAnimationFrame(raf);
  },[spin,rz]);

  const down = useCallback(e=>{
    setSpin(false);
    drag.current = { x:e.clientX, y:e.clientY, rx:rx.get(), rz:rz.get() };
    wrap.current?.setPointerCapture?.(e.pointerId);
  },[rx,rz]);
  const move = useCallback(e=>{
    const d = drag.current; if(!d) return;
    rx.set(Math.max(14, Math.min(84, d.rx + (e.clientY-d.y)*.35)));
    rz.set(d.rz - (e.clientX-d.x)*.4);
  },[rx,rz]);
  const up = useCallback(()=>{ drag.current = null; },[]);

  return html`
    <div className="board" ref=${wrap} onPointerDown=${down} onPointerMove=${move}
        onPointerUp=${up} onPointerLeave=${up}>
      <${M.div} className="board__plane" style=${{ transform: tf }}>
        <div className="pcb">
          <${Traces} ports=${ports} hot=${hot}/>
          <div className="die" style=${{ left:MCU.x-9+"%", top:MCU.y-7+"%" }}>
            <i className="die__top"><b>ESP32-P4</b><span>360 MHz</span></i>
            <i className="die__side"/>
          </div>
          ${ports.map(p=> html`<${Comp} key=${p.id} p=${p} hot=${hot===p.id}
            sel=${sel===p.id} onHover=${setHot} onPick=${onPick}/>`)}
          <div className="pcb__id">${E.BOARD.sku}</div>
        </div>
      <//>
      <div className="board__hint">
        <span>drag to turn</span>
        ${!spin && html`<button onClick=${e=>{ stop(e); setSpin(true); rx.set(58); }}>Reset view · UI</button>`}
      </div>
    </div>`;
}

/* ── the legend that makes the lights mean something ─────────────────────── */
export const BoardKey = () => html`
  <div className="bkey">
    ${[["live","In use","A bound interface carrying traffic right now."],
       ["partial","Partly bound","Some lines on this connector are assigned, some are free."],
       ["idle","Bound, quiet","Assigned but not carrying anything this minute."],
       ["free","Unassigned","Physically present, bound to nothing."]].map(([k,t,s])=> html`
      <div key=${k} className="bkey__r"><i style=${{ background:TONE[k], boxShadow:k==="live"||k==="partial"?`0 0 12px ${TONE[k]}`:"none" }}/>
        <span><b>${t}</b><em>${s}</em></span></div>`)}
  </div>`;

/* ── port inspector ──────────────────────────────────────────────────────── */
export function PortList({ ports, sel, onPick }){
  return html`
    <div className="ports">
      ${ports.map(p=> html`
        <button key=${p.id} className="port" data-on=${p.state} data-sel=${sel===p.id?"1":"0"}
            onClick=${()=>onPick(p.id)}>
          <i className="port__l"/>
          <span className="port__t"><b>${p.label}</b><em>${p.full}</em></span>
          <span className="port__b">${p.bound || "—"}</span>
        </button>`)}
    </div>`;
}

export function PortDetail({ p, onClose }){
  if(!p) return null;
  return html`
    <${M.div} className="pdet" initial=${{ opacity:0, y:8 }} animate=${{ opacity:1, y:0 }}
        exit=${{ opacity:0, y:6 }} transition=${FADE}>
      <div className="pdet__h">
        <i data-on=${p.state}/>
        <span><b>${p.full}</b><em>${p.label}</em></span>
        <button className="x" onClick=${onClose}><${Ico} d="close" w=${13}/></button>
      </div>
      <p className="note" style=${{ margin:"0 0 12px" }}>${p.detail}</p>
      <div className="kv"><span>Bound to</span><b className="hi">${p.bound || "nothing"}</b></div>
      <div className="kv"><span>Runtime lane</span><b className=${p.lane?"hi":""}>${p.lane || "—"}</b></div>
      <div className="kv"><span>State</span><b className=${p.state==="live"?"good":p.state==="free"?"":"warn"}>${p.state}</b></div>
    <//>`;
}
